import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from 'contexts/AuthContext';
import { BundlePromotionProvider } from 'contexts/BundlePromotionContext';
import { CartProvider } from 'contexts/CartContext';
import { FavouritesProvider } from 'contexts/FavouritesContext';
import { CurrencyProvider } from 'contexts/CurrencyContext';
import { ProductProvider } from 'contexts/ProductContext';
import SEO from 'components/common/SEO';
import Layout from 'components/layout/Layout';
import { organizationSchema, localBusinessSchema, webSiteSchema } from 'config/seoSchemas';
import ProtectedRoute from 'components/common/ProtectedRoute';
import GuestRoute from 'components/common/GuestRoute';
import CheckoutProtectedRoute from 'components/common/CheckoutProtectedRoute';
import OrderSuccessProtectedRoute from 'components/common/OrderSuccessProtectedRoute';
import ErrorBoundary, { RouteAwareErrorBoundary } from 'components/common/ErrorBoundary';
import ScrollToTop from 'components/common/ScrollToTop';

// Lazy load pages for code splitting
const HomePage = React.lazy(() => import('pages/home/HomePage'));
const ShopPage = React.lazy(() => import('pages/shop/ShopPage'));
const AboutPage = React.lazy(() => import('pages/about/AboutPage'));
const ContactPage = React.lazy(() => import('pages/contact/ContactPage'));
const ProductPage = React.lazy(() => import('pages/product/ProductPage'));
const LoginPage = React.lazy(() => import('pages/auth/LoginPage'));
const RegisterPage = React.lazy(() => import('pages/auth/RegisterPage'));
const ForgotPasswordPage = React.lazy(() => import('pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = React.lazy(() => import('pages/auth/ResetPasswordPage'));
const CartPage = React.lazy(() => import('pages/cart/CartPage'));
const CheckoutPage = React.lazy(() => import('pages/checkout/CheckoutPage'));
const OrderSuccessPage = React.lazy(() => import('pages/checkout/OrderSuccessPage'));
const AccountPage = React.lazy(() => import('pages/account/AccountPage'));
const PrivacyPage = React.lazy(() => import('pages/privacy/PrivacyPage'));
const CookiesPolicyPage = React.lazy(() => import('pages/privacy/CookiesPolicyPage'));
const TermsPage = React.lazy(() => import('pages/terms/TermsPage'));
const ReturnsPage = React.lazy(() => import('pages/returns/ReturnsPage'));
const MissionPage = React.lazy(() => import('pages/about/MissionPage'));
const BlogListPage = React.lazy(() => import('pages/blog/BlogListPage'));
const BlogPostPage = React.lazy(() => import('pages/blog/BlogPostPage'));
const ResourcesPage = React.lazy(() => import('pages/resources/ResourcesPage'));
const ErrorTestPage = React.lazy(() => import('pages/test/ErrorTestPage'));
const WorldpayTestPage = React.lazy(() => import('pages/test/WorldpayTestPage'));
const AdminApp = React.lazy(() => import('admin/AdminApp'));

// Loading component
const LoadingSpinner: React.FC = () => (
  <div className="flex justify-center items-center min-h-[50vh]">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
  </div>
);

function App() {
  return (
    <ErrorBoundary
      onError={(error, errorInfo) => {
        // Log error for tracking (will be replaced with error tracking service)
        if (process.env.NODE_ENV === 'production') {
          // TODO: Send to error tracking service
          // Example: Sentry.captureException(error, { contexts: { react: errorInfo } });
        }
      }}
    >
      <HelmetProvider>
        <CurrencyProvider>
          <AuthProvider>
            <CartProvider>
              <FavouritesProvider>
              <BundlePromotionProvider>
              <ProductProvider>
                <Router>
                <ScrollToTop />
                <Suspense fallback={<LoadingSpinner />}>
                  <Routes>
                    <Route path="/admin/*" element={<AdminApp />} />
                    <Route
                      path="*"
                      element={
                        <Layout>
                          <RouteAwareErrorBoundary>
                            <Routes>
                  <Route
                    path="/"
                    element={
                      <>
                        <SEO
                          title="Home"
                          description="Trusted Dental Supplies for Every Professional. Explore a wide range of high-quality dental products trusted by clinics, laboratories, and professionals."
                        >
                          <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
                          <script type="application/ld+json">{JSON.stringify(localBusinessSchema)}</script>
                          <script type="application/ld+json">{JSON.stringify(webSiteSchema)}</script>
                        </SEO>
                        <HomePage />
                      </>
                    }
                  />
                  <Route
                    path="/shop"
                    element={
                      <>
                        <SEO
                          title="Shop"
                          description="Browse our comprehensive collection of dental supplies, equipment, and materials for dental professionals."
                          keywords={['dental supplies', 'dental equipment', 'shop', 'products']}
                        />
                        <ShopPage />
                      </>
                    }
                  />
                  <Route
                    path="/product-category/:categorySlug/:productSlug"
                    element={<ProductPage />}
                  />
                  <Route path="/about" element={<AboutPage />} />
                  <Route path="/blog" element={<BlogListPage />} />
                  <Route path="/blog/:category/:slug" element={<BlogPostPage />} />
                  <Route path="/blog/:category" element={<BlogListPage />} />
                  <Route path="/resources" element={<ResourcesPage />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/privacy-policy" element={<PrivacyPage />} />
                  <Route path="/cookies-policy" element={<CookiesPolicyPage />} />
                  <Route path="/terms-and-conditions" element={<TermsPage />} />
                  <Route path="/mission-vision-values" element={<MissionPage />} />
                  <Route path="/returns-policy" element={<ReturnsPage />} />
                  <Route
                    path="/login"
                    element={
                      <GuestRoute>
                        <SEO
                          title="Sign In"
                          description="Sign in to your Abbey Dental Supplies account to access exclusive pricing and features."
                          noIndex
                        />
                        <LoginPage />
                      </GuestRoute>
                    }
                  />
                  <Route
                    path="/register"
                    element={
                      <GuestRoute>
                        <SEO
                          title="Register"
                          description="Create your Abbey Dental Supplies account to access exclusive pricing and features."
                          noIndex
                        />
                        <RegisterPage />
                      </GuestRoute>
                    }
                  />
                  <Route
                    path="/forgot-password"
                    element={
                      <GuestRoute>
                        <SEO
                          title="Forgot Password"
                          description="Reset your Abbey Dental Supplies account password."
                          noIndex
                        />
                        <ForgotPasswordPage />
                      </GuestRoute>
                    }
                  />
                  <Route
                    path="/reset-password/:token"
                    element={
                      <GuestRoute>
                        <SEO
                          title="Reset Password"
                          description="Set a new password for your Abbey Dental Supplies account."
                          noIndex
                        />
                        <ResetPasswordPage />
                      </GuestRoute>
                    }
                  />
                  <Route path="/cart" element={<CartPage />} />
                  <Route
                    path="/checkout"
                    element={
                      <CheckoutProtectedRoute>
                        <SEO
                          title="Checkout"
                          description="Complete your purchase securely with Abbey Dental Supplies."
                          noIndex
                        />
                        <CheckoutPage />
                      </CheckoutProtectedRoute>
                    }
                  />
                  <Route
                    path="/checkout/success"
                    element={
                      <OrderSuccessProtectedRoute>
                        <OrderSuccessPage />
                      </OrderSuccessProtectedRoute>
                    }
                  />
                  <Route
                    path="/account"
                    element={
                      <ProtectedRoute>
                        <SEO
                          title="My Account"
                          description="Manage your account, orders, and preferences with Abbey Dental Supplies."
                          noIndex
                        />
                        <AccountPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/special-offer"
                    element={
                      <>
                        <SEO
                          title="Special Offers"
                          description="Exclusive deals and special offers on dental supplies and equipment."
                        />
                        <ShopPage />
                      </>
                    }
                  />
                  {/* Test Pages - Remove in production or restrict access */}
                  {process.env.NODE_ENV === 'development' && (
                    <>
                      <Route
                        path="/test/error-boundary"
                        element={
                          <>
                            <SEO
                              title="Error Boundary Test"
                              description="Test page for Error Boundary functionality"
                              noIndex
                            />
                            <ErrorTestPage />
                          </>
                        }
                      />
                      <Route
                        path="/test/worldpay"
                        element={
                          <>
                            <SEO
                              title="Worldpay BG350 Test"
                              description="Test page for Worldpay BG350 integration"
                              noIndex
                            />
                            <WorldpayTestPage />
                          </>
                        }
                      />
                    </>
                  )}
                  {/* 404 Page */}
                  <Route
                    path="*"
                    element={
                      <>
                        <SEO
                          title="Page Not Found"
                          description="The page you're looking for doesn't exist."
                          noIndex
                        />
                        <div className="flex flex-col items-center justify-center min-h-[50vh] text-center">
                          <h1 className="text-4xl font-bold text-gray-900 mb-4">404 - Page Not Found</h1>
                          <p className="text-gray-600">The page you're looking for doesn't exist.</p>
                        </div>
                      </>
                    }
                  />
                    </Routes>
                          </RouteAwareErrorBoundary>
                        </Layout>
                      }
                    />
                  </Routes>
                </Suspense>
              </Router>
              </ProductProvider>
              </BundlePromotionProvider>
              </FavouritesProvider>
            </CartProvider>
          </AuthProvider>
        </CurrencyProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
